import React, { useState, useEffect, useRef } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { getDirtyProgress, getMeta } from '../db';
import { triggerAutoSync, subscribeSyncStatus, getSyncStatus } from '../sync';
import './SyncNotifications.css';

// 顶部全宽细横条: 离线优先 + 自动同步的状态提示
// 状态: 离线无新进度 / 离线有新进度 / 正在上传 / 正在合并 / 同步完成 / 同步失败 / 首次初始化
// 默认自动同步, 不弹窗; 仅同步失败显示"重试同步", 首次初始化失败显示"重新初始化"
export default function SyncNotifications() {
    const isOnline = useOnlineStatus();
    const [dirtyCount, setDirtyCount] = useState(0);
    const [initialized, setInitialized] = useState(true);
    const [status, setStatus] = useState(getSyncStatus());
    const [showSynced, setShowSynced] = useState(false);
    const hideTimer = useRef(null);
    const wasOffline = useRef(!navigator.onLine);

    // 轮询 dirty / 初始化状态
    useEffect(() => {
        const check = async () => {
            setDirtyCount((await getDirtyProgress()).length);
            setInitialized(!!(await getMeta('lastSyncAt')));
        };
        check();
        const timer = setInterval(check, 5000);
        return () => clearInterval(timer);
    }, []);

    // 订阅同步状态机
    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return subscribeSyncStatus((s) => {
            setStatus(s);
            if (s.state === 'synced') {
                setShowSynced(true);
                if (hideTimer.current) clearTimeout(hideTimer.current);
                hideTimer.current = setTimeout(() => setShowSynced(false), 4000);
            } else {
                setShowSynced(false);
            }
        });
    }, []);

    // 离线→联网恢复: 立即自动同步 (优先上传本地 dirty, 再合并云端)
    useEffect(() => {
        if (!isOnline) { wasOffline.current = true; return; }
        if (wasOffline.current) triggerAutoSync();
        wasOffline.current = false;
    }, [isOnline]);

    const isSunday = new Date().getDay() === 0;
    const retry = () => triggerAutoSync();

    // ---- 决定当前显示哪条横条 (优先级从高到低) ----
    let cls = '';           // 颜色类
    let text = null;
    let button = null;

    if (status.state === 'syncing') {
        if (!initialized) {
            cls = 'sync-state-init';
            text = '正在初始化离线学习数据，请稍候。';
        } else if (status.detail && status.detail.phase === 'upload') {
            cls = 'sync-state-upload';
            text = `正在同步离线进度：本地 ${status.detail.dirty} 条待上传，请不要关闭页面。`;
        } else {
            cls = 'sync-state-merge';
            text = '正在合并云端最新进度。';
        }
    } else if (status.state === 'failed') {
        cls = 'sync-state-failed';
        text = '同步失败，本地进度已保存，请稍后重试。';
        button = (
            <button className="sync-retry-btn" onClick={retry}>
                {initialized ? '重试同步' : '重新初始化'}
            </button>
        );
    } else if (showSynced) {
        cls = 'sync-state-synced';
        const d = status.detail || {};
        const extra = (d.pushed || 0) + (d.pulled || 0) > 0
            ? `（推送 ${d.pushed || 0} 条，合并 ${d.pulled || 0} 条）`
            : '';
        text = `同步完成，已合并最新进度${extra}。`;
    } else if (!isOnline) {
        if (dirtyCount > 0) {
            cls = 'sync-state-offline-dirty';
            text = `离线学习中，已有 ${dirtyCount} 条进度待同步，联网后会自动同步。`;
        } else {
            cls = 'sync-state-offline';
            text = '当前为离线模式，可使用已缓存课程。';
        }
    } else if (isSunday) {
        cls = 'sync-state-offline';
        text = '📅 今日周日，建议连接网络同步进度。';
    }

    if (!text) return null;

    return (
        <div className={`sync-bar ${cls}`}>
            <span className="sync-text">{text}</span>
            {button}
        </div>
    );
}
